/**
 * « Pour vous qui parlez <langue> » (D-122): the frequent traps of a lesson for a speaker of the
 * learner's mother tongue (false friends, hard sounds, word order), prepared once by the AI on the
 * server and kept, shown as a help with « à relire par un formateur » until a trainer approves it.
 */

import { academyCourseLessonsPitfallsCreate, getAcademyCourseLessonsPitfallsRetrieveQueryKey, useAcademyCourseLessonsPitfallsRetrieve } from "@agencyos/api-client";
import { type SpokenLanguage, languageLabel, parsePitfalls, pitfallText } from "@agencyos/core";
import { Badge, Button } from "@agencyos/ui-web";
import { useQueryClient } from "@tanstack/react-query";
import { Lightbulb } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useLessonExamParams } from "./exam";

const POLL_MS = 3000;

export function LessonPitfalls({ lessonId, code, language, auto }: { lessonId: string; code: string; language: SpokenLanguage | undefined; auto: boolean }) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const examParams = useLessonExamParams();
  const [busy, setBusy] = useState(false);
  const query = useAcademyCourseLessonsPitfallsRetrieve(lessonId, { language: code, ...examParams }, {
    query: {
      enabled: Boolean(code),
      retry: false,
      refetchInterval: (q) => {
        const data = q.state.data;
        return data?.status === 200 && parsePitfalls(data.data).status === "pending" && q.state.dataUpdateCount < 20 ? POLL_MS : false;
      },
    },
  });
  const value = useMemo(() => (query.data?.status === 200 ? parsePitfalls(query.data.data) : null), [query.data]);
  const tried = useRef(false);
  const prepare = useCallback(async () => {
    setBusy(true);
    try {
      const reply = await academyCourseLessonsPitfallsCreate(lessonId, { language: code }, examParams);
      queryClient.setQueryData(getAcademyCourseLessonsPitfallsRetrieveQueryKey(lessonId, { language: code, ...examParams }), reply);
    } finally {
      setBusy(false);
    }
  }, [lessonId, code, examParams, queryClient]);
  // « Toujours afficher dans ma langue »: asked once by itself
  useEffect(() => {
    if (auto && value?.status === "none" && !tried.current) {
      tried.current = true;
      void prepare();
    }
  }, [auto, value?.status, prepare]);
  if (!code || !value || value.status === "same") return null;
  const name = language ? languageLabel(language, i18n.language) : code;

  return (
    <section className="ac-pitfalls" aria-labelledby={`ac-pitfalls-${lessonId}`}>
      <h3 id={`ac-pitfalls-${lessonId}`} className="ac-pitfalls__title">
        <Lightbulb aria-hidden size={16} />
        <span>{t("academyCourse.pitfalls.title", { language: name })}</span>
        {value.status === "ready" && <Badge tone={value.review === "approved" ? "success" : "warning"}>{value.review === "approved" ? t("academyCourse.pitfalls.reviewed") : t("academyCourse.pitfalls.toReview")}</Badge>}
      </h3>
      {value.status === "none" || value.status === "failed" ? (
        <>
          <p className="ac-footnote">{t("academyCourse.pitfalls.intro")}</p>
          <Button variant="ghost" disabled={busy} onClick={() => void prepare()}>
            {busy ? t("common.loading") : t("academyCourse.pitfalls.action")}
          </Button>
        </>
      ) : null}
      {value.status === "pending" && (
        <p className="ac-status" role="status">
          {t("academyCourse.pitfalls.pending")}
        </p>
      )}
      {value.status === "unavailable" && <p className="ac-footnote">{value.message || t("academyCourse.pitfalls.unavailable")}</p>}
      {value.status === "ready" && (
        <>
          {value.message && (
            <p className="ac-note" role="note">
              {value.message}
            </p>
          )}
          <ul className="ac-pitfalls__list">
            {value.items.map((item) => {
              const text = pitfallText(item, i18n.language);
              return (
                <li key={item.title}>
                  <strong>{item.title}</strong>
                  <p>{text.main}</p>
                  {text.native && (
                    <p className="ac-pitfalls__native" lang={code} dir={language?.rtl ? "rtl" : undefined}>
                      {text.native}
                    </p>
                  )}
                  {item.example && (
                    <p className="ac-pitfalls__example">
                      <span className="ac-label">{t("academyCourse.pitfalls.example")}</span> <em>{item.example}</em>
                    </p>
                  )}
                </li>
              );
            })}
          </ul>
          {value.beta && <p className="ac-footnote">{t("academyCourse.tongue.beta")}</p>}
        </>
      )}
    </section>
  );
}
